import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import Datepicker from "react-tailwindcss-datepicker";
import { Breadcrumbs, Button } from "@material-tailwind/react";
import dayjs from "dayjs";
import qs from "qs";

import service, { WinLoseSorting } from "../../service";
import useDatePicker from "../../hook/useDatePicker";
import useFocusUser from "./useFocusUser";
import WinloseTable from "../../Component/WinloseTable";
import { memo, NamedExoticComponent, useCallback, useState } from "react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";

export type FooterProps = {
  betAmount: number;
  tickets: number;
  winLose: number;
};

type DateRange = {
  startDate: Date;
  endDate: Date;
};

type ExportVariables = {
  startDate: string;
  endDate: string;
  username?: string;
  sorting?: WinLoseSorting;
};

const Footer: NamedExoticComponent<FooterProps> = memo(
  ({ betAmount, tickets, winLose }) => {
    return (
      <tr>
        <th colSpan={2}>Total</th>
        <th>{tickets && Number(tickets.toFixed(2)).toLocaleString("en-US")}</th>
        <th>
          {betAmount && Number(betAmount.toFixed(2)).toLocaleString("en-US")}
        </th>
        <th>{winLose && Number(winLose.toFixed(2)).toLocaleString("en-US")}</th>
      </tr>
    );
  }
);

const getExportErrorMessage = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;

  if (status === 400) {
    return "Export requires a date range shorter than one calendar month.";
  }

  return "Unable to export the report. Please try again.";
};

const Report = () => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { value, handleChange } = useDatePicker();

  const { data: focusUser, onChange: onChangeFocusUser } = useFocusUser();
  const [submittedDateRange, setSubmittedDateRange] =
    useState<DateRange | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const reportSorting: WinLoseSorting | undefined = sorting[0]
    ? ({
        [sorting[0].id]: sorting[0].desc ? -1 : 1,
      } as WinLoseSorting)
    : undefined;
  const onSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setSorting((currentSorting) => {
      const nextSorting =
        typeof updater === "function" ? updater(currentSorting) : updater;

      return nextSorting.length ? [nextSorting[nextSorting.length - 1]] : [];
    });
  }, []);

  const onClickMember = useCallback(
    (member: string) => {
      if (submittedDateRange === null) {
        return;
      }

      const queryString = qs.stringify({
        username: member,
        startDate: dayjs(submittedDateRange.startDate).format("DD-MM-YYYY"),
        endDate: dayjs(submittedDateRange.endDate).format("DD-MM-YYYY"),
        dateField: "settleDate",
      });
      navigate(`/ticket?${queryString}`);
    },
    [navigate, submittedDateRange]
  );

  const winLose = useQuery({
    queryFn: () =>
      service.getWinLose({
        startDate: submittedDateRange!.startDate.toISOString(),
        endDate: submittedDateRange!.endDate.toISOString(),
        username: focusUser,
        ...(reportSorting ? { sorting: reportSorting } : {}),
      }),
    queryKey: [
      "winLose",
      submittedDateRange
        ? dayjs(submittedDateRange.startDate).format("DD-MM-YYYY")
        : null,
      submittedDateRange
        ? dayjs(submittedDateRange.endDate).format("DD-MM-YYYY")
        : null,
      focusUser,
      reportSorting,
    ],
    enabled: submittedDateRange !== null,
    onSuccess: () => {
      if (submittedDateRange === null) {
        return;
      }

      const queryString = qs.stringify({
        username: focusUser,
        startDate: dayjs(submittedDateRange.startDate).format("DD-MM-YYYY"),
        endDate: dayjs(submittedDateRange.endDate).format("DD-MM-YYYY"),
      });

      setSearchParams(queryString);
    },
  });

  const exportMutation = useMutation({
    mutationFn: (variables: ExportVariables) =>
      service.exportWinLose(variables),
    onSuccess: (file) => {
      const objectUrl = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "new-winLose.xlsx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        if (window.URL.revokeObjectURL) {
          window.URL.revokeObjectURL(objectUrl);
        }
      }, 0);
    },
  });

  const onSubmit = () => {
    const nextDateRange = {
      startDate: value.startDate,
      endDate: value.endDate,
    };
    const nextStartDate = dayjs(nextDateRange.startDate).format("DD-MM-YYYY");
    const nextEndDate = dayjs(nextDateRange.endDate).format("DD-MM-YYYY");
    const hasSameDateRange =
      submittedDateRange !== null &&
      dayjs(submittedDateRange.startDate).format("DD-MM-YYYY") ===
        nextStartDate &&
      dayjs(submittedDateRange.endDate).format("DD-MM-YYYY") === nextEndDate;

    setSubmittedDateRange(nextDateRange);
    if (!exportMutation.isLoading) {
      exportMutation.reset();
    }

    if (hasSameDateRange) {
      void winLose.refetch();
    }
  };

  const currentStartDate = dayjs(value.startDate);
  const currentEndDate = dayjs(value.endDate);
  const isCurrentDateRangeValid =
    currentStartDate.isValid() &&
    currentEndDate.isValid() &&
    !currentEndDate.isBefore(currentStartDate);
  const isExportDisabled = !isCurrentDateRangeValid || exportMutation.isLoading;
  const onExport = () => {
    if (isExportDisabled) {
      return;
    }

    exportMutation.mutate({
      startDate: value.startDate.toISOString(),
      endDate: value.endDate.toISOString(),
      username: focusUser || undefined,
      ...(reportSorting ? { sorting: reportSorting } : {}),
    });
  };

  return (
    <div className="min-w-0 space-y-4">
      {winLose.data?.parentList && (
        <div className="-ml-2 mb-1 max-w-full overflow-x-auto">
          <Breadcrumbs className="min-w-max bg-white/80 px-2 py-2 shadow-sm">
            {winLose.data?.parentList.map((parent: { username: string }) => (
              <b
                key={parent.username}
                onClick={() => {
                  onChangeFocusUser(parent.username);
                }}
              >
                {parent.username}
              </b>
            ))}
          </Breadcrumbs>
        </div>
      )}

      <div className="w-full sm:max-w-sm">
        <Datepicker
          primaryColor="green"
          value={value}
          onChange={(value) => {
            handleChange(value);
          }}
          showShortcuts={true}
          separator="to"
          displayFormat={"DD-MM-YYYY"}
          maxDate={dayjs().endOf("month").toDate()}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={onSubmit} className="w-full sm:w-auto">
          Submit
        </Button>
        <Button
          type="button"
          onClick={onExport}
          disabled={isExportDisabled}
          aria-label="Export Excel"
          className="w-full sm:w-auto"
        >
          {exportMutation.isLoading ? "Exporting..." : "Export Excel"}
        </Button>
        <span className="text-xs text-gray-500">
          Export supports a range shorter than one calendar month.
        </span>
        {!isCurrentDateRangeValid && (
          <span role="alert" className="text-xs text-red-600">
            Choose a valid start and end date before exporting.
          </span>
        )}
      </div>

      {exportMutation.isError && (
        <p role="alert" className="text-sm text-red-600">
          {getExportErrorMessage(exportMutation.error)}
        </p>
      )}

      <WinloseTable
        Footer={
          <Footer
            betAmount={winLose.data?.total?.betAmount || 0}
            tickets={winLose.data?.total?.tickets || 0}
            winLose={winLose.data?.total?.winLose || 0}
          />
        }
        isLoading={winLose.isInitialLoading}
        data={winLose.data?.data || []}
        context={{ onChangeFocusUser, focusUser, onClickMember }}
        sorting={sorting}
        onSortingChange={onSortingChange}
        manualSorting
      />
    </div>
  );
};

export default Report;
